/** Commission rule editor (ST-03): per-staff or per-role rate/flat rules.
 *  Owner/manager only (route-guarded). Validation mirrors CommissionRuleSchema:
 *  at least one of staffId/appRole, at least one of rateBps/flatAmount. */
import type { CommissionBasis, Role } from '@gym/shared';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, CurrencyDisplay, Spinner } from '../../components/ui.js';

import {
  type CommissionRuleRow,
  useCommissionRules,
  useCreateCommissionRule,
  useDeactivateCommissionRule,
  useStaffList,
} from './hooks/useStaff.js';

const BASES: CommissionBasis[] = ['membership_sold', 'pt_session', 'retail_sale', 'class_taught'];

/** A rule targets EITHER a specific staff member OR a whole role (plan 08 Task 8). */
type Scope = 'role' | 'staff';

export function CommissionRulesForm(): ReactNode {
  const { t } = useTranslation();
  const rules = useCommissionRules();
  const staff = useStaffList({ isActive: true });
  const createRule = useCreateCommissionRule();
  const deactivate = useDeactivateCommissionRule();

  const [adding, setAdding] = useState(false);
  const [scope, setScope] = useState<Scope>('role');
  const [appRole, setAppRole] = useState<Role | ''>('trainer');
  const [staffId, setStaffId] = useState('');
  const [basis, setBasis] = useState<CommissionBasis>('membership_sold');
  const [rateType, setRateType] = useState<'percent' | 'fixed'>('percent');
  // The input collects a human PERCENT (e.g. 5 = 5%); we convert to basis points
  // (rateBps = percent * 100) only at submit time. Default 5% == 500 bps.
  const [ratePercent, setRatePercent] = useState(5);
  const [flatAmount, setFlatAmount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setError(null);
    // Exactly one target: a specific staff member (staff scope) or a role.
    if (scope === 'role' && !appRole) {
      setError(t('errors.commission_rule_requires_staff_or_role'));
      return;
    }
    if (scope === 'staff' && !staffId) {
      setError(t('errors.commission_rule_requires_staff_or_role'));
      return;
    }
    const hasRate = rateType === 'percent' ? ratePercent > 0 : flatAmount > 0;
    if (!hasRate) {
      setError(t('errors.commission_rule_requires_rate_or_flat'));
      return;
    }
    try {
      await createRule.mutateAsync({
        // staff-specific rule (staffId, no appRole) OR role-level (appRole, no staffId)
        staffId: scope === 'staff' ? staffId : null,
        appRole: scope === 'role' && appRole ? appRole : null,
        basis,
        rateBps: rateType === 'percent' ? Math.round(ratePercent * 100) : null,
        flatAmount: rateType === 'fixed' ? flatAmount : null,
        isActive: true,
      });
      setAdding(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h2 style={{ margin: 0, flex: 1 }}>{t('staff.commissions.title')}</h2>
        <Button onClick={() => setAdding((v) => !v)}>{t('staff.commissions.add_rule')}</Button>
      </div>

      {adding ? (
        <div
          role="group"
          aria-label={t('staff.commissions.add_rule')}
          style={{ display: 'grid', gap: '0.5rem', marginBottom: '1rem' }}
        >
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              variant={scope === 'role' ? 'primary' : 'secondary'}
              onClick={() => setScope('role')}
            >
              {t('staff.commissions.scope_role')}
            </Button>
            <Button
              variant={scope === 'staff' ? 'primary' : 'secondary'}
              onClick={() => setScope('staff')}
            >
              {t('staff.commissions.scope_staff')}
            </Button>
          </div>
          {scope === 'role' ? (
            <label>
              {t('staff.form.role')}
              <select
                aria-label="appRole"
                value={appRole}
                onChange={(e) => setAppRole(e.target.value as Role | '')}
              >
                {(['owner', 'manager', 'front_desk', 'trainer'] as const).map((r) => (
                  <option key={r} value={r}>
                    {t('staff.roles.' + r)}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label>
              {t('staff.commissions.scope_staff')}
              <select
                aria-label="staffId"
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
              >
                <option value="">{t('staff.commissions.pick_staff')}</option>
                {(staff.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.first_name} {s.last_name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>
            {t('staff.commissions.title')}
            <select
              aria-label="basis"
              value={basis}
              onChange={(e) => setBasis(e.target.value as CommissionBasis)}
            >
              {BASES.map((b) => (
                <option key={b} value={b}>
                  {t('staff.commissions.basis.' + b)}
                </option>
              ))}
            </select>
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              variant={rateType === 'percent' ? 'primary' : 'secondary'}
              onClick={() => setRateType('percent')}
            >
              {t('staff.commissions.rate_type_percent')}
            </Button>
            <Button
              variant={rateType === 'fixed' ? 'primary' : 'secondary'}
              onClick={() => setRateType('fixed')}
            >
              {t('staff.commissions.rate_type_fixed')}
            </Button>
          </div>
          {rateType === 'percent' ? (
            <label>
              %{' '}
              <input
                type="number"
                step="0.01"
                aria-label="ratePercent"
                value={ratePercent}
                onChange={(e) => setRatePercent(Number(e.target.value))}
              />
            </label>
          ) : (
            <label>
              MKD{' '}
              <input
                type="number"
                aria-label="flatAmount"
                value={flatAmount}
                onChange={(e) => setFlatAmount(Number(e.target.value))}
              />
            </label>
          )}
          {error ? (
            <p role="alert" style={{ color: 'var(--color-danger)' }}>
              {error}
            </p>
          ) : null}
          <Button onClick={() => void submit()} disabled={createRule.isPending}>
            {t('common.save')}
          </Button>
        </div>
      ) : null}

      {rules.isLoading ? (
        <Spinner />
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--color-muted)' }}>
              <th>{t('staff.form.role')}</th>
              <th>{t('staff.commissions.title')}</th>
              <th>{t('staff.commissions.total')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {(rules.data ?? []).map((r: CommissionRuleRow) => {
              const target = r.staff_id
                ? ((s) => (s ? `${s.first_name} ${s.last_name}` : '—'))(
                    (staff.data ?? []).find((x) => x.id === r.staff_id),
                  )
                : t('staff.roles.' + (r.app_role ?? 'member'));
              return (
                <tr key={r.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td>{target}</td>
                  <td>{t('staff.commissions.basis.' + r.basis)}</td>
                  <td>
                    {r.rate_bps != null ? (
                      `${(r.rate_bps / 100).toFixed(2)}%`
                    ) : (
                      <CurrencyDisplay minorUnits={r.flat_amount ?? 0} />
                    )}
                  </td>
                  <td>
                    <Button variant="ghost" onClick={() => deactivate.mutate(r.id)}>
                      {t('staff.commissions.deactivate_rule')}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}
